const STORAGE_PREFIX = "sonicbox-marketing-v1";

const isBrowser = () => typeof window !== "undefined";

const getSessionStorage = () => (isBrowser() ? window.sessionStorage : null);
const getLocalStorage = () => (isBrowser() ? window.localStorage : null);

const readJson = (storage, key, fallback) => {
  if (!storage) return fallback;

  try {
    const rawValue = storage.getItem(key);
    if (!rawValue) return fallback;
    return JSON.parse(rawValue);
  } catch (_error) {
    return fallback;
  }
};

const writeJson = (storage, key, value) => {
  if (!storage) return;

  try {
    storage.setItem(key, JSON.stringify(value));
  } catch (_error) {
    // Storage can fail in private browsing or quota-restricted contexts.
  }
};

const normalizeKeyPart = (value, fallback = "na") => {
  if (value === undefined || value === null || value === "") return fallback;
  return String(value).replace(/:/g, "_");
};

export const buildMarketingStorageKey = ({
  radioid,
  userid,
  campaignId,
  creativeId,
  revision,
}) =>
  [
    STORAGE_PREFIX,
    normalizeKeyPart(radioid, "unscoped"),
    normalizeKeyPart(userid, "anon"),
    normalizeKeyPart(campaignId),
    normalizeKeyPart(creativeId),
    normalizeKeyPart(revision, "default"),
  ].join(":");

const getStorageForScope = (scope) =>
  scope === "session" ? getSessionStorage() : getLocalStorage();

const getPeriodStart = (scope, now) => {
  const currentDate = new Date(now);

  if (scope === "user_day" || scope === "radio_day") {
    currentDate.setHours(0, 0, 0, 0);
    return currentDate.getTime();
  }

  return 0;
};

const getEventList = (state, key, scope, now) => {
  const values = Array.isArray(state?.[key]) ? state[key] : [];
  const periodStart = getPeriodStart(scope, now);

  return values.filter((entry) => {
    const at = Number(entry?.at);
    return Number.isFinite(at) && at >= periodStart;
  });
};

export const getMarketingFrequencyState = ({ frequency, key, now = Date.now() }) => {
  const storage = getStorageForScope(frequency?.scope);
  const state = readJson(storage, key, {
    impressions: [],
    closes: [],
    clicks: [],
  });

  return {
    impressions: getEventList(state, "impressions", frequency?.scope, now),
    closes: getEventList(state, "closes", frequency?.scope, now),
    clicks: getEventList(state, "clicks", frequency?.scope, now),
  };
};

export const canShowMarketingCreative = ({
  frequency,
  key,
  now = Date.now(),
  stopAfterClick = false,
  stopAfterClickSeconds = null,
}) => {
  if (!frequency || !key) return false;

  if (stopAfterClick || Number(stopAfterClickSeconds) > 0) {
    const storage = getStorageForScope(frequency.scope);
    const persistedState = readJson(storage, key, {});
    const clickTimes = Array.isArray(persistedState.clicks)
      ? persistedState.clicks
          .map((entry) => Number(entry?.at))
          .filter(Number.isFinite)
      : [];
    const lastClickAt = clickTimes[clickTimes.length - 1];
    const clickStopMs = Number(stopAfterClickSeconds) * 1000;
    const hasActiveClickStop = stopAfterClick
      ? !!lastClickAt
      : !!lastClickAt && now - lastClickAt < clickStopMs;

    if (hasActiveClickStop) return false;
  }

  const state = getMarketingFrequencyState({ frequency, key, now });
  const maxImpressions = Number(frequency.maxImpressions);
  const cooldownMs = Number(frequency.cooldownSeconds ?? 0) * 1000;

  if (Number.isFinite(maxImpressions) && state.impressions.length >= maxImpressions) {
    return false;
  }

  const lastImpression = state.impressions[state.impressions.length - 1];
  if (
    cooldownMs > 0 &&
    lastImpression?.at &&
    now - Number(lastImpression.at) < cooldownMs
  ) {
    return false;
  }

  return true;
};

const recordMarketingEvent = ({
  frequency,
  key,
  eventName,
  payload = {},
  now = Date.now(),
}) => {
  if (!frequency || !key || !eventName) return null;

  const storage = getStorageForScope(frequency.scope);
  const state = getMarketingFrequencyState({ frequency, key, now });
  const nextState = {
    impressions: state.impressions,
    closes: state.closes,
    clicks: state.clicks,
    [eventName]: [
      ...(Array.isArray(state[eventName]) ? state[eventName] : []),
      {
        at: now,
        ...payload,
      },
    ],
  };

  writeJson(storage, key, nextState);
  return nextState;
};

export const recordMarketingImpression = (params) =>
  recordMarketingEvent({ ...params, eventName: "impressions" });

export const recordMarketingClose = (params) =>
  recordMarketingEvent({ ...params, eventName: "closes" });

export const recordMarketingClick = (params) =>
  recordMarketingEvent({ ...params, eventName: "clicks" });

export const clearMarketingFrequencyState = (key, scope = "session") => {
  const storage = getStorageForScope(scope);
  if (!storage || !key) return;
  storage.removeItem(key);
};
